import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Database, RefreshCw, Server, HardDrive,
  Activity, AlertTriangle, BarChart3, Zap, Layers,
  Wifi, WifiOff
} from 'lucide-react';

import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Skeleton } from '../ui/skeleton';
import { useAuth, API } from '../../contexts/AuthContext';
import { getErrorMessage } from '../../utils/errorHandler';

const formatUptime = (seconds) => {
  if (!seconds && seconds !== 0) return 'N/A';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  parts.push(`${mins}m`);
  return parts.join(' ');
};

const StatCard = ({ icon: Icon, label, value, sublabel, variant = 'default', loading }) => {
  const variantStyles = {
    default: 'border-gray-200',
    success: 'border-green-300 bg-green-50',
    warning: 'border-yellow-300 bg-yellow-50',
    error: 'border-red-300 bg-red-50',
    info: 'border-blue-300 bg-blue-50',
  };

  return (
    <Card className={`p-4 border-2 ${variantStyles[variant] || variantStyles.default} hover:shadow-lg transition-all duration-200`}>
      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-3 w-20" />
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              variant === 'error' ? 'bg-red-100 text-red-600' :
              variant === 'success' ? 'bg-green-100 text-green-600' :
              variant === 'warning' ? 'bg-yellow-100 text-yellow-600' :
              variant === 'info' ? 'bg-blue-100 text-blue-600' :
              'bg-gray-100 text-gray-600'
            }`}>
              <Icon className="w-4 h-4" />
            </div>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</span>
          </div>
          <p className="text-2xl font-bold text-gray-800">{value}</p>
          {sublabel && <p className="text-xs text-gray-500 mt-1">{sublabel}</p>}
        </>
      )}
    </Card>
  );
};

const CacheStatsTab = () => {
  const { getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds
  const intervalRef = useRef(null);

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/admin/cache-stats`, {
        headers: getAuthHeaders()
      });
      setStats(response.data);
    } catch (err) {
      const msg = getErrorMessage(err, 'Error al cargar estad�sticas de cach�');
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Auto-refresh interval
  useEffect(() => {
    if (autoRefresh) {
      loadStats(); // Fetch immediately when turned on
      intervalRef.current = setInterval(() => {
        loadStats();
      }, AUTO_REFRESH_INTERVAL);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh, loadStats]);

  const isConnected = stats?.status === 'connected';
  const hitMiss = stats?.hit_miss || {};
  const totalOps = (hitMiss.keyspace_hits || 0) + (hitMiss.keyspace_misses || 0);
  const hitRatio = hitMiss.hit_ratio_percent;

  return (
    <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 border border-orange-100">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
            <Database className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Estado de Cach� (Redis)</h2>
            <p className="text-sm text-gray-600">M�tricas y estado del servidor Redis</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500 hidden sm:inline">Auto-refresh</span>
            <Switch
              checked={autoRefresh}
              onCheckedChange={setAutoRefresh}
              className={autoRefresh ? 'data-[state=checked]:bg-purple-600' : ''}
            />
            <span className="text-xs text-gray-400 w-12">
              {autoRefresh ? '30s' : 'Off'}
            </span>
          </div>
          <Button
            onClick={loadStats}
            disabled={loading}
            className="bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 rounded-xl shadow-md"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {error && !loading && (
        <Card className="p-4 mb-6 border-2 border-red-300 bg-red-50">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-red-600" />
            <div>
              <p className="font-semibold text-red-800">Error de conexi�n</p>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          icon={isConnected ? Wifi : WifiOff}
          label="Estado"
          value={isConnected ? 'Conectado' : 'Desconectado'}
          sublabel={isConnected ? 'Redis responde correctamente' : 'Revisar configuraci�n'}
          variant={isConnected ? 'success' : 'error'}
          loading={loading}
        />
        <StatCard
          icon={Server}
          label="Versi�n"
          value={stats?.server?.redis_version || '-'}
          sublabel={stats?.server?.role ? `Rol: ${stats.server.role}` : undefined}
          variant="info"
          loading={loading}
        />
        <StatCard
          icon={Activity}
          label="Tiempo activo"
          value={formatUptime(stats?.server?.uptime_in_seconds)}
          sublabel={`${stats?.server?.connected_clients || 0} clientes conectados`}
          variant="default"
          loading={loading}
        />
        <StatCard
          icon={Zap}
          label="Comandos/seg"
          value={stats?.server?.ops_per_sec != null ? `${stats.server.ops_per_sec}` : '-'}
          sublabel={`${stats?.server?.total_commands_processed?.toLocaleString() || 0} totales`}
          variant="default"
          loading={loading}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-5 border-2 border-blue-200 hover:shadow-lg transition-all">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <HardDrive className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="font-bold text-gray-800 text-lg">Memoria</h3>
          </div>
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Usada</span>
                <span className="font-semibold text-gray-800">{stats?.memory?.used_memory_human || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Pico</span>
                <span className="font-semibold text-gray-800">{stats?.memory?.used_memory_peak_human || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">M�x. configurada</span>
                <span className="font-semibold text-gray-800">{stats?.memory?.maxmemory_human || 'Sin l�mite'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Fragmentaci�n</span>
                <span className="font-semibold text-gray-800">
                  {stats?.memory?.fragmentation_ratio != null ? `${parseFloat(stats.memory.fragmentation_ratio).toFixed(2)}x` : 'N/A'}
                </span>
              </div>
            </div>
          )}
        </Card>

        <Card className="p-5 border-2 border-green-200 hover:shadow-lg transition-all">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="font-bold text-gray-800 text-lg">Hit / Miss Ratio</h3>
          </div>
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ) : (
            <>
              <div className="mb-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-semibold text-gray-600">Efectividad</span>
                  <span className={`text-lg font-bold ${
                    hitRatio == null ? 'text-gray-400' :
                    hitRatio >= 95 ? 'text-green-600' :
                    hitRatio >= 80 ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {hitRatio != null ? `${hitRatio}%` : 'Sin datos'}
                  </span>
                </div>
                {hitRatio != null && (
                  <Progress value={hitRatio} className="h-3" />
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-green-50 rounded-lg p-2 text-center">
                  <p className="text-xs text-gray-500">Hits</p>
                  <p className="font-bold text-green-700">{hitMiss.keyspace_hits?.toLocaleString() || 0}</p>
                </div>
                <div className="bg-red-50 rounded-lg p-2 text-center">
                  <p className="text-xs text-gray-500">Misses</p>
                  <p className="font-bold text-red-700">{hitMiss.keyspace_misses?.toLocaleString() || 0}</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2 text-center">
                {totalOps > 0
                  ? `Basado en ${totalOps.toLocaleString()} operaciones totales`
                  : 'No hay operaciones registradas a�n'}
              </p>
            </>
          )}
        </Card>
      </div>

      <Card className="p-5 border-2 border-orange-200 hover:shadow-lg transition-all">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
            <Layers className="w-5 h-5 text-orange-600" />
          </div>
          <h3 className="font-bold text-gray-800 text-lg">Claves en Cach�</h3>
          <Badge variant="outline" className="ml-2 text-xs">
            {stats?.keys?.total_keys != null ? `${stats.keys.total_keys} totales` : '...'}
          </Badge>
        </div>
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-3/4" />
          </div>
        ) : stats?.keys?.by_prefix ? (
          <div className="space-y-2">
            {Object.entries(stats.keys.by_prefix).map(([prefix, count]) => (
              <div key={prefix} className="flex items-center justify-between p-3 rounded-xl bg-gray-50 hover:bg-orange-50 transition-colors">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-orange-500 to-red-500" />
                  <span className="text-sm font-medium text-gray-700">{prefix}</span>
                </div>
                <Badge variant="secondary" className="text-xs font-bold">
                  {count}
                </Badge>
              </div>
            ))}
            {Object.keys(stats.keys.by_prefix).length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">No hay claves en cach�</p>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500 text-center py-4">No disponible</p>
        )}
      </Card>
    </div>
  );
};

export default CacheStatsTab;
